import React, { useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import {
  loadBusinessEstablishmentsSummary,
  buildBusinessEstablishmentsNarrative,
  BUSINESS_ESTABLISHMENTS_TABLE,
  SECTOR_TOTAL_FOR_POLICY,
  SIZE_ALL_ESTABLISHMENTS,
  type BusinessEstablishmentsSummary,
} from '../../services/businessEstablishmentsService';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export type BusinessEstablishmentsChartView = 'sector' | 'size' | 'sector_trend';

export interface BusinessEstablishmentsSectionProps {
  location: string;
  geofips?: string | null;
}

const BusinessEstablishmentsSection: React.FC<BusinessEstablishmentsSectionProps> = ({ location }) => {
  const [summary, setSummary] = useState<BusinessEstablishmentsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<string>('overview');
  const [chartView, setChartView] = useState<BusinessEstablishmentsChartView>('sector');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await loadBusinessEstablishmentsSummary(location);
        if (!cancelled) setSummary(s);
      } catch {
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const narrative = useMemo(
    () => (summary ? buildBusinessEstablishmentsNarrative(location, summary) : ''),
    [location, summary]
  );

  const sectorBarData = useMemo(() => {
    if (!summary?.topSectors?.length) return null;
    const labels = summary.topSectors.map((t) => t.sectorLabel);
    const data = summary.topSectors.map((t) => t.establishments);
    return {
      labels,
      datasets: [
        {
          label: 'Establishments',
          data,
          backgroundColor: 'rgba(59, 130, 246, 0.75)',
          borderColor: 'rgb(29, 78, 216)',
          borderWidth: 1,
        },
      ],
    };
  }, [summary]);

  const sizeBarData = useMemo(() => {
    if (!summary?.sizeBreakdown?.length) return null;
    const labels = summary.sizeBreakdown.map((t) => t.sizeLabel);
    const data = summary.sizeBreakdown.map((t) => t.establishments);
    return {
      labels,
      datasets: [
        {
          label: 'Establishments',
          data,
          backgroundColor: 'rgba(22, 101, 52, 0.75)',
          borderColor: 'rgb(20, 83, 45)',
          borderWidth: 1,
        },
      ],
    };
  }, [summary]);

  const sectorStackedAreaData = useMemo(() => {
    const s = summary?.sectorStackedArea;
    if (!s || !s.datasets.length) return null;
    return {
      labels: s.years.map(String),
      datasets: s.datasets.map((d) => ({
        label: d.label,
        data: d.data,
        backgroundColor: d.backgroundColor,
        borderColor: d.borderColor,
        borderWidth: 1,
        fill: true,
        tension: 0.25,
        pointRadius: 0,
        stack: 'establishments',
      })),
    };
  }, [summary?.sectorStackedArea]);

  const sectorBarOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: summary?.latestYear
            ? `Establishments by industry sector (${summary.latestYear})`
            : 'Establishments by sector',
        },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed?: { x?: number } }) =>
              `${ctx.parsed?.x != null ? Number(ctx.parsed.x).toLocaleString() : '—'} establishments`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: 'Establishments' },
          ticks: {
            callback: (v: string | number) => Number(v).toLocaleString(),
          },
        },
      },
    }),
    [summary?.latestYear]
  );

  const sizeBarOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: summary?.latestYear
            ? `Establishments by employment size class (${summary.latestYear}, ${SECTOR_TOTAL_FOR_POLICY})`
            : 'Establishments by size',
        },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed?: { x?: number } }) =>
              `${ctx.parsed?.x != null ? Number(ctx.parsed.x).toLocaleString() : '—'} establishments`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: 'Establishments' },
          ticks: {
            callback: (v: string | number) => Number(v).toLocaleString(),
          },
        },
      },
    }),
    [summary?.latestYear]
  );

  const sectorTrendOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { position: 'top' as const },
        title: {
          display: true,
          text: 'Establishments by sector over time (stacked)',
        },
        tooltip: {
          callbacks: {
            footer: (items: { parsed: { y: number } }[]) => {
              const sum = items.reduce((s, it) => s + (Number(it.parsed.y) || 0), 0);
              return `Total: ${sum.toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          title: { display: true, text: 'Year' },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          title: { display: true, text: 'Establishments' },
          ticks: {
            callback: (v: string | number) => Number(v).toLocaleString(),
          },
        },
      },
    }),
    []
  );

  const keyMetricsEl =
    loading || !summary ? null : (
      <KeyMetrics>
        {summary.hasEstablishmentData && summary.totalEstablishments != null && (
          <KeyMetric
            value={summary.totalEstablishments.toLocaleString()}
            label={`All establishments (${summary.latestYear ?? 'latest'}) — policy row`}
          />
        )}
        {summary.workersPerEstablishment != null && (
          <KeyMetric
            value={summary.workersPerEstablishment.toFixed(1)}
            label="Workers per establishment"
          />
        )}
        {summary.establishmentsPer1kPop != null && (
          <KeyMetric
            value={summary.establishmentsPer1kPop.toFixed(1)}
            label="Establishments per 1,000 residents"
          />
        )}
        {summary.establishmentsPer1kJobs != null && (
          <KeyMetric
            value={summary.establishmentsPer1kJobs.toFixed(1)}
            label="Establishments per 1,000 workplace jobs"
          />
        )}
        {!summary.hasEstablishmentData && summary.workplaceJobsLatest != null && (
          <KeyMetric
            value={summary.workplaceJobsLatest.toLocaleString()}
            label={`Workplace jobs (LODES, ${summary.workplaceJobsYear ?? 'yr'})`}
          />
        )}
      </KeyMetrics>
    );

  const chartInner =
    loading ? (
      <p style={{ padding: 24, color: '#6b7280' }}>Loading business establishment data…</p>
    ) : chartView === 'sector_trend' ? (
      sectorStackedAreaData ? (
        <div style={{ height: 400, position: 'relative' }}>
          <Line data={sectorStackedAreaData} options={sectorTrendOptions} />
        </div>
      ) : (
        <p style={{ padding: 24, color: '#6b7280' }}>
          Need multiple years of sector rows (size “{SIZE_ALL_ESTABLISHMENTS}”) to show the stacked trend.
        </p>
      )
    ) : chartView === 'sector' && sectorBarData ? (
      <div style={{ height: 380, position: 'relative' }}>
        <Bar data={sectorBarData} options={sectorBarOptions} />
      </div>
    ) : chartView === 'size' && sizeBarData ? (
      <div style={{ height: 380, position: 'relative' }}>
        <Bar data={sizeBarData} options={sizeBarOptions} />
      </div>
    ) : (
      <p style={{ padding: 24, color: '#6b7280' }}>
        {summary?.hasEstablishmentData
          ? chartView === 'sector'
            ? 'No sector-level breakdown available for the latest year.'
            : 'No employment-size breakdown available for the latest year (check for “Total for all sectors” rows).'
          : 'Connect `business_establishments` for this geography to chart sector and size mix, or use workplace jobs (above) as context.'}
      </p>
    );

  const layoutChartType: 'bar' | 'line' =
    chartView === 'sector_trend' ? 'line' : 'bar';

  return (
    <ChartLayout
      title="Business Establishments"
      description="Employer business locations (establishments) show where commercial building energy demand and employer-focused programs concentrate. Sector and employment-size views use Census-style business patterns; totals align with the row used for building electrification cost scaling."
      chartType={layoutChartType}
      keyMetrics={keyMetricsEl}
      chartSelector={{
        options: [
          { value: 'sector', label: 'By sector (latest)' },
          { value: 'sector_trend', label: 'Sectors over time (stacked)' },
          { value: 'size', label: 'By employment size' },
        ],
        value: chartView,
        onChange: (v) => setChartView(v as BusinessEstablishmentsChartView),
      }}
      chartContent={
        <div id="business-establishments-chart">
          {chartInner}
        </div>
      }
      overviewContent={
        <OverviewContent
          title="Overview"
          description="Establishment counts, sector mix, and employment-size structure for the selected geography."
        >
          {summary?.fetchWarning ? (
            <p style={{ color: '#b45309', marginBottom: 12 }}>Data note: {summary.fetchWarning}</p>
          ) : null}
          <p>{narrative}</p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to climate policy"
          description="Commercial buildings and employer programs scale with the number, size, and type of businesses."
        >
          <div className="methodology-details-simple">
            <h5>Building decarbonization &amp; electrification</h5>
            <p>
              The app uses the all-establishments total (<em>{SECTOR_TOTAL_FOR_POLICY}</em> ×{' '}
              <em>{SIZE_ALL_ESTABLISHMENTS}</em>) to scale commercial gas and electricity when estimating
              per-establishment intensity for the building electrification cost layer. Sector mix highlights
              where audits, technical assistance, and incentives may have the largest footprint (e.g. food
              service, retail, offices).
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Transportation &amp; commute</h5>
            <p>
              Job concentration and establishment counts shape commute VMT and workplace charging needs.
              Comparing establishments to LODES workplace jobs distinguishes many small sites from a few
              large employers.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity"
          description="Business structure intersects with labor, wages, and who can access upgrades."
        >
          <div className="methodology-details-simple">
            <h5>Small business &amp; workforce</h5>
            <p>
              A large share of establishments in small employment-size classes often means many thin-margin
              businesses with limited capacity to finance upgrades without grants, on-bill financing, or
              targeted technical assistance.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Geographic distribution</h5>
            <p>
              Sector mix can concentrate local pollution and energy burdens (e.g. logistics, industry).
              Pair establishment context with tract-level demographics when siting facilities or designing
              building codes.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Methods &amp; data sources"
          description="How establishment metrics and charts are built in this app."
          dataSources={[
            `Supabase table \`${BUSINESS_ESTABLISHMENTS_TABLE}\` (geofips, year, sector, size, count, num_employees)`,
            'U.S. Census Bureau — County Business Patterns (CBP) and related published sector/size tables (typically lagged vs. current year)',
            'LEHD Origin-Destination Employment Statistics (LODES) — lodes_employees_workplace for workplace job counts',
            'population — latest population for per-capita rates',
          ]}
          methodology={[
            `Policy / headline total: row with sector “${SECTOR_TOTAL_FOR_POLICY}” and size “${SIZE_ALL_ESTABLISHMENTS}”; establishment count = the count column. Passed into building electrification commercial intensity when available.`,
            'Workers per establishment = published employee total ÷ that same row’s establishment count when `num_employees` is numeric.',
            'Employees: `num_employees` is used only when it is a numeric string (comma-separated digits allowed). Non-numeric published text is ignored for the employee total metric.',
            `Sector bar: latest year, size = “${SIZE_ALL_ESTABLISHMENTS}”, sector ≠ total row, sorted by count.`,
            'Stacked area: for each year, same sector rows; top 10 sectors by latest-year count plus “All other sectors.”',
            `Size chart: latest year, sector = “${SECTOR_TOTAL_FOR_POLICY}”, size ≠ “${SIZE_ALL_ESTABLISHMENTS}”, sorted by implied minimum employment bracket.`,
            'Geofips matching tries stripped leading zeros and 7-digit padding variants.',
            'Establishments per 1,000 jobs uses LODES workplace totals; per 1,000 residents uses the population table.',
            'If the table is missing or empty for a location, the section falls back to LODES workplace jobs only.',
          ]}
        />
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      onDownloadPNG={() => {
        const canvas = document.querySelector('#business-establishments-chart canvas') as HTMLCanvasElement;
        if (canvas) {
          const link = document.createElement('a');
          const suffix =
            chartView === 'sector' ? 'By_Sector' : chartView === 'size' ? 'By_Size' : 'Sectors_Stacked_Time';
          link.download = `${formatFilename(location, `Business_Establishments_${suffix}`)}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      }}
      onDownloadCSV={() => {
        if (!summary) return;
        const rows: Record<string, string | number | null>[] = [
          {
            location,
            metric: 'total_establishments_policy_row',
            year: summary.latestYear,
            value: summary.totalEstablishments,
            note: `${SECTOR_TOTAL_FOR_POLICY} / ${SIZE_ALL_ESTABLISHMENTS}`,
          },
          {
            location,
            metric: 'workers_per_establishment',
            year: summary.latestYear,
            value: summary.workersPerEstablishment,
            note: null,
          },
          {
            location,
            metric: 'establishments_per_1000_residents',
            year: summary.latestYear,
            value: summary.establishmentsPer1kPop,
            note: null,
          },
          {
            location,
            metric: 'establishments_per_1000_workplace_jobs',
            year: summary.latestYear,
            value: summary.establishmentsPer1kJobs,
            note: null,
          },
          {
            location,
            metric: 'total_employees_numeric',
            year: summary.latestYear,
            value: summary.totalEmployees,
            note: null,
          },
          {
            location,
            metric: 'workplace_jobs_lodes',
            year: summary.workplaceJobsYear,
            value: summary.workplaceJobsLatest,
            note: null,
          },
          {
            location,
            metric: 'population',
            year: null,
            value: summary.populationLatest,
            note: null,
          },
        ];
        if (summary.sectorStackedArea) {
          const { years, datasets } = summary.sectorStackedArea;
          years.forEach((y, yi) => {
            datasets.forEach((d) => {
              rows.push({
                location,
                metric: 'sector_trend',
                year: y,
                value: d.data[yi] ?? 0,
                note: d.label,
              });
            });
          });
        }
        summary.topSectors.forEach((t, i) => {
          rows.push({
            location,
            metric: `sector_rank_${i + 1}`,
            year: summary.latestYear,
            value: t.establishments,
            note: t.sectorLabel,
          });
        });
        summary.sizeBreakdown.forEach((t, i) => {
          rows.push({
            location,
            metric: `size_rank_${i + 1}`,
            year: summary.latestYear,
            value: t.establishments,
            note: t.sizeLabel,
          });
        });
        downloadDataAsCSV(rows, formatFilename(location, 'Business_Establishments_Data'));
      }}
      pngFilename={formatFilename(
        location,
        chartView === 'sector'
          ? 'Business_Establishments_By_Sector'
          : chartView === 'size'
            ? 'Business_Establishments_By_Size'
            : 'Business_Establishments_Sectors_Stacked_Time'
      )}
      csvFilename={formatFilename(location, 'Business_Establishments_Data')}
    />
  );
};

export default BusinessEstablishmentsSection;
